import { useApiClient } from '~/api-client';
import { useMutation } from '@tanstack/react-query';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { EventPanelFormType, EventPanelForm, EventPanelResource } from '@mark43/cad-api';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { BlankModal, ButtonGroup, ModalBody, ModalFooter, ModalHeader, VStack } from '@cad-arc';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { FormEventAutocomplete } from '../forms/integrated-form-controls/form-event-autocomplete';
import { EventOption } from '../forms/inputs/event-autocomplete';
import { WorkspacePanel } from '../panels/workspace-panel';
import { useOpenEventPanel } from './use-open-event-panel';
import { filterInvalidEventPanelEntities } from './event-panel-form/filter-invalid-event-panel-entities';

type TransferEventInfoForm = {
    targetEventIdOption: null | EventOption;
};

const requiredRule = {
    required: 'Required',
};

export type TransferEventInfoModalComponentType = typeof TransferEventInfoModal;
export function TransferEventInfoModal(props: {
    eventForm: UseFormReturn<EventPanelFormType>;
    sourceEventPanel: WorkspacePanel;
}): React.ReactElement {
    const apiClient = useApiClient();
    const modalControls = useFullscreenModalControls();
    const openEventPanel = useOpenEventPanel();

    const transferEventInfoForm = useForm<TransferEventInfoForm>({
        defaultValues: {
            targetEventIdOption: null,
        },
    });

    const transferEventMutation = useMutation(
        // eslint-disable-next-line unused-imports/no-unused-vars
        (args: TransferEventInfoForm) => {
            return apiClient.request<EventPanelResource.TransferEvent>({
                method: 'POST',
                url: 'event_panel/transfer_event/',
                data: {
                    targetEventId: transferEventInfoForm.getValues().targetEventIdOption!.value,
                    eventPanelForm: filterInvalidEventPanelEntities(
                        props.eventForm.getValues()
                    ) as EventPanelForm,
                },
            });
        },
        {
            onSuccess() {
                props.sourceEventPanel.close();
                openEventPanel({
                    eventId: transferEventInfoForm.getValues().targetEventIdOption!.value,
                });
                modalControls.close();
            },
        }
    );

    return (
        <IntegratedFormProvider mutation={transferEventMutation} form={transferEventInfoForm}>
            <BlankModal isOpen={true} onClose={() => modalControls.close()}>
                <ModalHeader>Transfer Event Info</ModalHeader>
                <ModalBody>
                    <FormBody>
                        <VStack w="100%">
                            <FormEventAutocomplete
                                label="Search"
                                control={transferEventInfoForm.control}
                                searchForCallForService={true}
                                name="targetEventIdOption"
                                rules={requiredRule}
                                helpText="Search by Event #, CFS"
                            />
                        </VStack>
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <ButtonGroup>
                        <FormCancelButton
                            onClick={() => {
                                modalControls.close();
                            }}
                        />
                        <FormSaveButton>Confirm</FormSaveButton>
                    </ButtonGroup>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
}
